import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import type { TopBarTypes } from './types';

import styled, { css } from 'styled-components';

import useWebsiteEditor from 'shared/hooks/useWebsiteEditor';
import { defaultWebValue, filterBackgroundColor } from 'shared/helpers/website';
import { generateAccessKey, sanitizeRichText } from 'shared/helpers/utils';
import { COLOR_BLUE_3 } from 'shared/helpers/colors';

import { Wrapper, Button, Download } from '../Common';
import useSales from 'shared/hooks/useSales';
import useIsEmpty from 'shared/hooks/useIsEmpty';

const Container = styled.div<{ backgroundColor: string }>`
  background-color: ${(props) => props.backgroundColor};
  padding: 18px 0;
`;

export const TitleContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  ${({ theme: { $width } }) => $width <= 800 && 'flex-direction: column; gap:10px;'}
`;

const Title = styled.div<{ isEmpty?: boolean; editable?: boolean }>`
  font-size: ${({ theme }) => theme.$subheadlineFont};
  font-weight: 500;
  max-width: 95%;
  line-height: 1.4em;
  cursor: text;
  position: relative;
  min-width: 30%;

  ${({ isEmpty, editable }) =>
    isEmpty && editable
      ? `
    &:not(:focus):before {
      content: attr(data-placeholder);
      cursor: text;
      color: #aaa;
      position: absolute;
      display: flex;
      justify-content: center;
      align-items: center;
    }
  `
      : `
    &:before {
      content: none;
    } 
  `}

  ${({ theme: { $width } }) =>
    $width <= 768 &&
    `
    text-align: center;
    padding-right: 0px;
  `}
`;

const BtnInner = styled.div`
  flex-shrink: 0;
  ${({ theme: { $width } }) =>
    $width <= 800 &&
    `
    width: 100%;
    display: flex;
    justify-content: center;
  `}
`;

const buttonStyles = css`
  position: relative;
  z-index: 99;
  padding-top: 17px;
  padding-bottom: 17px;
  max-width: 480px;

  ${({ theme: { $width } }) =>
    $width <= 800 &&
    `
    margin-top: 8px;
    width: 70%;
    margin: 0 auto;
    padding-top: 20px;
    padding-bottom: 20px;
  `}

  @media (max-width: 575px) {
    display: block;
    margin: 0 auto;
  }
`;

const StyledButton = styled((props) => <Button {...props} />)`
  ${buttonStyles}
`;

const DownloadStyled = styled(Download)`
  ${buttonStyles}
`;

export const V1: FC<TopBarTypes> = (props) => {
  const {
    title = defaultWebValue,
    button = defaultWebValue,
    hidden,
    secondaryColor = COLOR_BLUE_3,
    meta,
    textColor,
    customBackgroundColor,
    order = 0
  } = props;

  const ref = useRef<HTMLDivElement | null>(null);

  const {
    updateActiveElement,
    resetActiveElement,
    containerClassname,
    websiteAssembler,
    editable
  } = useWebsiteEditor();

  const [isSticky, setIsSticky] = useState<boolean>(false);
  const { type, salesPageValues, deliveryPageValues, purchaseElementId, isDashboard } = useSales();

  const { isEmpty } = useIsEmpty(meta, 'title');

  // Accessibility improvements
  const titleAccessKey = useMemo(() => generateAccessKey('tbt', order), [order]);
  const buttonAccessKey = useMemo(() => generateAccessKey('tbb', order), [order]);
  const skipLinkId = useMemo(() => `skip-to-topbar-${meta?.['accessorKey'] || 'section'}`, [meta]);
  const titleId = useMemo(
    () => title.id || `topbar-title-${meta?.['accessorKey'] || 'heading'}`,
    [title.id, meta]
  );
  const regionLabel = useMemo(() => {
    return isSticky ? 'Sticky navigation bar' : 'Top navigation bar';
  }, [isSticky]);

  const handleEditError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Top bar edit error: ${error instanceof Error ? error.message : 'Edit action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  const handleModalError = (error: Error | unknown) => {
    // Announce error to screen readers
    const errorAnnouncement = document.createElement('div');
    errorAnnouncement.setAttribute('aria-live', 'polite');
    errorAnnouncement.setAttribute('aria-atomic', 'true');
    errorAnnouncement.className = 'sr-only';
    errorAnnouncement.textContent = `Button action error: ${error instanceof Error ? error.message : 'Action failed'}`;
    document.body.appendChild(errorAnnouncement);
    setTimeout(() => document.body.removeChild(errorAnnouncement), 1000);
  };

  useEffect(() => {
    const updateSticky = () => {
      const checkValues = type === 'sales' ? salesPageValues : deliveryPageValues;
      if (!ref.current || !ref.current.parentElement || websiteAssembler || !checkValues) return;
      const values = checkValues[meta['accessorKey']];

      const minOrder = Math.min(
        ...Object.values(checkValues)
          .filter((item) => typeof item === 'object')
          .map((item) => item['order'])
          .filter((order) => typeof order === 'number')
      );

      if (typeof values === 'object' && values.order !== minOrder) return;

      const top = ref.current.parentElement.getBoundingClientRect().top;

      if (top < 0) {
        ref.current.classList.add('pw-sticky-topbar');
        setIsSticky(true);

        // Announce sticky state to screen readers
        const stickyAnnouncement = document.createElement('div');
        stickyAnnouncement.setAttribute('aria-live', 'polite');
        stickyAnnouncement.setAttribute('aria-atomic', 'true');
        stickyAnnouncement.className = 'sr-only sticky-announcement';
        stickyAnnouncement.textContent = 'Navigation bar is now sticky';

        const existsAlready = document.querySelector('.sticky-announcement');
        if (!existsAlready) {
          document.body.appendChild(stickyAnnouncement);
          setTimeout(() => {
            if (stickyAnnouncement.parentNode) {
              document.body.removeChild(stickyAnnouncement);
            }
          }, 1000);
        }
      } else if (top >= 0) {
        ref.current.classList.remove('pw-sticky-topbar');
        setIsSticky(false);
      }
    };

    window.addEventListener('scroll', updateSticky);
    window.addEventListener('resize', updateSticky);

    return () => {
      window.removeEventListener('scroll', updateSticky);
      window.removeEventListener('resize', updateSticky);
    };
  }, [deliveryPageValues, websiteAssembler, meta, salesPageValues, type]);

  const href = useMemo(() => {
    if (meta['url']) return meta['url'];
    return purchaseElementId;
  }, [meta, purchaseElementId]);

  const resolvedBackgroundColor = useMemo(() => {
    if (customBackgroundColor) {
      return customBackgroundColor;
    }
    return secondaryColor;
  }, [customBackgroundColor, secondaryColor]);

  // Determine button type for accessibility
  const buttonType = useMemo(() => {
    if (meta['download']) return 'download';
    if (meta['community']) return 'community link';
    if (meta['event']) return 'event registration';
    if (meta['url']) return 'external link';
    if (type === 'delivery') return 'delivery action';
    return 'purchase button';
  }, [meta, type]);

  return (
    <Container
      id="sales_topbar_component"
      backgroundColor={resolvedBackgroundColor}
      ref={ref}
      className={containerClassname}
      style={{
        display: hidden && !websiteAssembler ? 'none' : 'block',
        opacity: hidden ? 0.3 : 1
      }}
      role="banner"
      aria-label={regionLabel}
      aria-hidden={hidden && !websiteAssembler}>
      {/* Skip link for keyboard navigation */}
      <span className="sr-only">
        <a href={`#${skipLinkId}`}>Skip to top bar</a>
      </span>

      <Wrapper id={skipLinkId}>
        <TitleContainer role="navigation" aria-label="Top bar navigation">
          <Title
            dangerouslySetInnerHTML={{
              __html: sanitizeRichText(title.val, isDashboard)
            }}
            id={titleId}
            style={{ color: filterBackgroundColor(resolvedBackgroundColor, textColor) }}
            isEmpty={isEmpty}
            {...(isDashboard
              ? {
                  onClick: (e: React.MouseEvent<HTMLElement>) => {
                    try {
                      updateActiveElement(e);
                    } catch (error) {
                      handleEditError(error);
                    }
                  },
                  onBlur: resetActiveElement,
                  'data-accessor-parent': meta?.['accessorKey'],
                  'data-accessorkey': 'title',
                  'data-placeholder': '+',
                  editable: true
                }
              : {
                  'aria-label': isEmpty || !title.val?.trim() ? 'Top bar message' : undefined,
                  accessKey: titleAccessKey,
                  title: `Press Alt+${titleAccessKey} to focus top bar message`,
                  tabIndex: 0,
                  role: 'heading',
                  'aria-level': 1
                })}
          />

          <BtnInner role="group" aria-label={`Action button area - ${buttonType}`}>
            {!['form', 'recurring_form', 'upsell_vip_form'].includes(href) ||
            meta['url'] ||
            type === 'delivery' ? (
              <DownloadStyled
                textColor={textColor}
                button={button}
                meta={meta}
                isBotPosition
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': titleId
                  // 'aria-pressed': isButtonActionModalOpen ? 'true' : 'false'
                })}
              />
            ) : (
              <StyledButton
                to={purchaseElementId}
                button={button}
                meta={meta}
                isBotPosition
                outerOnClick={() => {
                  if (editable) {
                    try {
                      // empty needs only for error
                    } catch (error) {
                      handleModalError(error);
                    }
                  }
                }}
                {...(!isDashboard && {
                  accessKey: buttonAccessKey,
                  title: `Press Alt+${buttonAccessKey} to activate this ${buttonType}`,
                  'aria-describedby': titleId
                  // 'aria-pressed': isButtonActionModalOpen ? 'true' : 'false'
                })}
              />
            )}
          </BtnInner>
        </TitleContainer>
      </Wrapper>

      {/* Screen reader announcement for sticky state */}
      {isSticky && (
        <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
          Navigation bar is currently sticky and will remain visible while scrolling
        </div>
      )}
    </Container>
  );
};

V1.displayName = 'Topbar V1';
